import { useEffect, useRef } from 'react';

import { useNavigate, useSearchParams } from 'react-router';

import { config } from '@/config';
import { useLazyGetMeQuery, useSocialLoginMutation } from '@/features/auth/store/api';
import { needsOrgSetup, setToken, signOut, updateUser } from '@/features/auth/store/authSlice';
import { extractServerError, extractServerErrorCode, notify } from '@/lib/utils';
import { ROUTES } from '@/routes';
import { useAppDispatch } from '@/store/hooks';

import { LOGIN_STATUS_PARAM, LOGIN_STATUS_PENDING } from '../constants';
import { consumeSocialAuthAttempt } from '../socialAuthState';

/**
 * Handles the OAuth2 callback from Google / GitHub.
 *
 * The OAuth provider redirects back here with `?code=<authorization_code>&state=<state>`.
 * The `state` must match the attempt LoginForm stored when the user clicked the provider button,
 * which also tells which provider it was and holds the PKCE code verifier the backend needs to
 * exchange the code.
 *
 * This view exchanges the code for a token via the backend and then
 * redirects to the dashboard on success, or back to login on failure.
 */
export function SocialAuthCallback() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') ?? '';
  const state = searchParams.get('state');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [socialLogin] = useSocialLoginMutation();
  const [triggerGetMe] = useLazyGetMeQuery();
  // The stored attempt is single use, so the exchange must not run twice (StrictMode re-runs effects).
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const attempt = consumeSocialAuthAttempt(state);
    if (!code) {
      navigate(ROUTES.login, { replace: true });
      return;
    }
    if (!attempt) {
      notify.error('This sign-in attempt could not be verified. Please try again.');
      navigate(ROUTES.login, { replace: true });
      return;
    }

    const doLogin = async () => {
      try {
        const result = await socialLogin({
          provider: attempt.provider,
          code,
          redirect_uri: config.socialRedirectUri,
          code_verifier: attempt.codeVerifier,
        }).unwrap();
        dispatch(setToken(result.access_token));
        const me = await triggerGetMe().unwrap();
        if (me.user.is_demo) {
          dispatch(signOut());
          notify.error('Your account does not have access to the developer portal.');
          navigate(ROUTES.login, { replace: true });
          return;
        }
        dispatch(updateUser(me.user));
        navigate(needsOrgSetup(me.user) ? ROUTES.orgSetup : ROUTES.dashboard, { replace: true });
      } catch (err) {
        dispatch(signOut());
        if (extractServerErrorCode(err) === 'ERR_ACCOUNT_NOT_ACTIVATED') {
          // LoginForm shows the "waiting for approval" box from this param.
          const params = new URLSearchParams({ [LOGIN_STATUS_PARAM]: LOGIN_STATUS_PENDING });
          navigate(`${ROUTES.login}?${params.toString()}`, { replace: true });
          return;
        }
        notify.error(extractServerError(err, 'Social login failed. Please try again.'));
        navigate(ROUTES.login, { replace: true });
      }
    };

    doLogin();
    // socialLogin/dispatch/navigate/triggerGetMe are stable refs — intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state]);

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4">
      <div
        className="size-10 animate-spin rounded-full border-4 border-white/20 border-t-white"
        role="status"
        aria-label="Completing sign-in"
      />
      <p className="text-sm text-white/60">Completing sign-in…</p>
    </div>
  );
}
